<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onUnmounted,
  nextTick,
  onBeforeUnmount,
  onBeforeUpdate,
  onUpdated,
} from 'vue'
import type { Editor } from '@tiptap/vue-3'
import { ChevronDownIcon } from '@gohighlevel/ghl-icons/24/outline'
import { FontFamilyDropdown } from '../FontFamilyDropdown'
import { UIButton } from '@gohighlevel/ghl-ui'
import { Transaction } from '@tiptap/pm/state'

export interface TextStyleAttributes {
  fontFamily?: string
  fontSize?: string
  color?: string
  indent?: number
}

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
    default: false,
  },
  editor: {
    type: Object as () => Editor,
    required: true,
  },
  fallbackFont: {
    type: String,
    required: true,
  },
  loadedFonts: {
    type: Array as () => string[],
  },
  element: {
    type: String,
    required: true,
  },
  fontsToLoad: {
    type: Array as () => string[],
    default: () => [],
  },
})

const selectedFontFamily = ref<string | undefined>(props.fallbackFont)
const emit = defineEmits(['fonts-loaded-from-control'])
const showFontsValue = ref(false)
const dropdownButtonRef = ref<HTMLElement | null>(null)
const dropdownPosition = ref({ left: '0px', top: '0px' })
const dropdownContentRef = ref<HTMLElement | null>(null)

const updateDropdownPosition = () => {
  if (!dropdownButtonRef.value) return

  const rect = dropdownButtonRef.value.getBoundingClientRect()
  const isTop = (rect.y / window.innerHeight) * 100 > 70

  dropdownPosition.value = {
    left: `${rect.left}px`,
    top: isTop ? `${rect.top - 10}px` : `${rect.bottom + 10}px`,
  }
}

const toggleShowFontValue = () => {
  showFontsValue.value = !showFontsValue.value
  if (showFontsValue.value) {
    // Update position immediately when opening
    updateDropdownPosition()

    // Add click outside listener after dropdown is shown
    nextTick(() => {
      document.addEventListener('mousedown', handleClickOutside)
    })
  } else {
    // Remove listener when dropdown is closed
    document.removeEventListener('mousedown', handleClickOutside)
  }
}

const handleClickOutside = (event: MouseEvent) => {
  // Close dropdown if clicked outside of it
  if (
    showFontsValue.value &&
    dropdownButtonRef.value &&
    dropdownContentRef.value &&
    !dropdownButtonRef.value.contains(event.target as Node) &&
    !dropdownContentRef.value.contains(event.target as Node)
  ) {
    showFontsValue.value = false
    document.removeEventListener('mousedown', handleClickOutside)
  }
}

// Handle window resize to reposition dropdown if needed
const onWindowResize = () => {
  if (showFontsValue.value) {
    updateDropdownPosition()
  }
}

const popupPosition = computed(() => {
  let position = 'bottom'
  const clientRect = dropdownButtonRef.value?.getBoundingClientRect()

  if (clientRect && window) {
    const scrollPercentage = (clientRect.y / window.innerHeight) * 100

    if (scrollPercentage > 70) position = 'top'
  }
  return position
})

const onFontsLoaded = (newFonts: string[]) => {
  emit('fonts-loaded-from-control', newFonts)
}

const getExtensionDetails = ({
  editor,
}: {
  editor: Editor
  transaction: Transaction
}) => {
  const textStyleAttributes = editor.getAttributes(
    'textStyle'
  ) as TextStyleAttributes

  selectedFontFamily.value = props.fallbackFont

  const fontFamily = textStyleAttributes?.fontFamily?.trim() ?? ''

  if (fontFamily) {
    if (props.loadedFonts) {
      selectedFontFamily.value = fontFamily
    } else {
      // Extract the first font family (primary font)
      const primaryFont = fontFamily.match(
        /^(?:'([^']+)'|"([^"]+)"|([^,\s]+))/
      )?.[0]
      if (primaryFont) {
        // Remove quotes if present
        selectedFontFamily.value = primaryFont.replace(/^['"]|['"]$/g, '')
      }
    }
  }
}

onBeforeUpdate(() => {
  props?.editor?.off('transaction', getExtensionDetails)
})

// For multi editor
onUpdated(() => {
  props?.editor?.on('transaction', getExtensionDetails)
})

onBeforeUnmount(() => {
  props?.editor?.off('transaction', getExtensionDetails)
})

onMounted(() => {
  window.addEventListener('resize', onWindowResize)
  props?.editor?.on('transaction', getExtensionDetails)
  if (props.fallbackFont) {
    props?.editor.setOptions({
      editorProps: {
        attributes: state => {
          return {
            ...state.doc.attrs,
            style: 'font-family:' + props.fallbackFont, // Default font-family
          }
        },
      },
    })
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', onWindowResize)
  document.removeEventListener('mousedown', handleClickOutside)
})
</script>

<template>
  <div
    v-if="show"
    class="custom-font-family-dropdown-button relative"
    id="custom-font-family-dropdown-button"
    ref="dropdownButtonRef"
  >
    <UIButton
      id="text-editor-toolbar-item-font-family"
      title="Font family"
      size="small"
      :text="true"
      :active="showFontsValue"
      class="min-w-16 h-7 px-1.5 flex flex-row justify-between items-center p-1.5 rounded font-fam max-w-32"
      @click="toggleShowFontValue"
    >
      <span
        :style="{ fontFamily: selectedFontFamily }"
        class="font-inter font-semibold text-sm"
        >{{ selectedFontFamily }}</span
      >
      <ChevronDownIcon class="w-4 h-4 chevron_down_icon mt-0.5 ml-1" />
    </UIButton>

    <teleport to="body">
      <div
        v-if="showFontsValue"
        ref="dropdownContentRef"
        class="font-dropdown-container"
        :style="{
          position: 'fixed',
          zIndex: 9999,
          left: dropdownPosition.left,
          top: dropdownPosition.top,
          transform: popupPosition === 'top' ? 'translateY(-100%)' : 'none',
        }"
      >
        <FontFamilyDropdown
          v-if="show"
          :key="'editor-toolbar-font-family-control'"
          :editor="editor"
          :fallback-font="fallbackFont"
          :loaded-fonts="loadedFonts"
          @fonts-loaded="onFontsLoaded"
          :selectedFontFamily="selectedFontFamily"
          :fonts-to-load="fontsToLoad"
        />
      </div>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
.font-fam {
  ::v-deep(.n-input) {
    --n-border: 1px solid rgb(224, 224, 230) !important;
    --n-font-size: 14px !important;
    --n-height: 28px !important;
    --n-text-color: rgb(51, 54, 57) !important;
    --n-border-radius: 3px !important;
    --n-padding: 2px !important;
    background-color: #fff !important;
    --n-text-color: rgb(51, 54, 57) !important;
    --n-text-color-focus: rgb(51, 54, 57) !important;
    --n-text-color-pressed: rgb(51, 54, 57) !important;
    --n-text-color-hover: rgb(51, 54, 57) !important;
  }
  ::v-deep(.n-button--default-type.hl-active-btn) {
    --n-background-color: #fff !important;
  }
}
</style>
